import type {
  ConnectorTool,
  McpConnectorUser,
  McpToolCallPage,
  McpToolUsage,
  McpUserUsage,
} from "@/lib/api";
import { formatNumber, formatShare } from "@/lib/format";
import { i18n } from "@/lib/i18n";
import { CALLS_PATH, FAILURES_PATH } from "@/lib/issues";
import { parsePeriod, type Period, PERIODS } from "@/lib/period";
import { sortRows } from "@/lib/table-sort";

/** The health view's old address (`/analytics?view=health`), kept for bookmarks. */
const OLD_HEALTH_VIEW = "health";

/**
 * Where an old address of the health view goes: the Fouten page, with the
 * period it named (`?view=health&days=30`). Null for any other address of
 * Statistieken's first page.
 */
export function movedHealthView(search: string): { pathname: string; search: string } | null {
  const params = new URLSearchParams(search);
  if (params.get("view") !== OLD_HEALTH_VIEW) {
    return null;
  }
  const days = parsePeriod(params.get("days"));
  return { pathname: FAILURES_PATH, search: days === PERIODS[0] ? "" : `?days=${days}` };
}

/** The period's search for a link to another page of Statistieken: `?days=30`, nothing at the default. */
export function periodSearch(days: Period): string {
  return days === PERIODS[0] ? "" : `?days=${days}`;
}

/** What a link from the Fouten list to an issue's page carries: the list's search as it was left. */
export interface FailuresListState {
  failuresSearch: string;
}

/** The state of a link from the Fouten list to an issue: its search (`?status=…&sort=…`). */
export function failuresListState(search: string): FailuresListState {
  return { failuresSearch: search };
}

/**
 * The search of the issue page's way back to Fouten: the list's own search
 * as the link to the issue left it (`FailuresListState`: its filters and its
 * sort), else none, over the issue page's period, which may have changed
 * there. A custom range does not apply to the list.
 */
export function failuresBackSearch(state: unknown, days: Period): string {
  const listed =
    typeof state === "object" &&
    state !== null &&
    "failuresSearch" in state &&
    typeof state.failuresSearch === "string"
      ? state.failuresSearch
      : "";
  const params = new URLSearchParams(listed);
  params.delete("from");
  params.delete("to");
  if (days === PERIODS[0]) {
    params.delete("days");
  } else {
    params.set("days", String(days));
  }
  const search = params.toString();
  return search ? `?${search}` : "";
}

/**
 * The Oproepen page filtered on one tool or one person (by their Microsoft
 * id), over the same period: where a row of Overzicht's tables leads.
 */
export function callsLink(filter: { tool?: string; person?: string }, days: Period): string {
  const params = new URLSearchParams();
  if (filter.tool) {
    params.set("tool", filter.tool);
  }
  if (filter.person) {
    params.set("person", filter.person);
  }
  if (days !== PERIODS[0]) {
    params.set("days", String(days));
  }
  const search = params.toString();
  return search ? `${CALLS_PATH}?${search}` : CALLS_PATH;
}

/**
 * Failed calls as a count with their share of all calls: "12 (3%)", "1
 * (<1%)", or the count alone without calls. Every failure counts, so there is
 * no threshold and no rounding a small number away (`formatShare`).
 */
export function formatFailures(errors: number, calls: number): string {
  if (calls <= 0) {
    return formatNumber(errors);
  }
  return `${formatNumber(errors)} (${formatShare(errors / calls)})`;
}

/** A person as Overzicht's table lists them: their period's calls, their name and their block. */
export interface PersonRow {
  /** Stable React key: the oid, or "unidentified" for calls without sign-in. */
  key: string;
  /** Entra object id; null for calls without a signed-in user. */
  userOid: string | null;
  name: string | null;
  email: string | null;
  calls: number;
  errors: number;
  lastCallAt: string | null;
  blocked: boolean;
}

/** The period's people, from the usage (`per_user`), busiest first as the API sends them. */
export function personRows(usage: readonly McpUserUsage[]): PersonRow[] {
  return usage.map((used) => ({
    key: used.user_oid ?? "unidentified",
    userOid: used.user_oid ?? null,
    name: used.user_name?.trim() || null,
    email: used.user_email ?? null,
    calls: used.calls,
    errors: used.errors,
    lastCallAt: used.last_call_at,
    blocked: used.blocked ?? false,
  }));
}

/** Who a person is, for people: the name, else the email, else the oid, else "Zonder aanmelding". */
export function personLabel(row: Pick<PersonRow, "name" | "email" | "userOid">): string {
  return row.name ?? row.email ?? row.userOid ?? i18n.t("analytics.withoutSignIn");
}

/** One choice of the people filter: a person by their Microsoft id, with their name and email. */
export interface PersonOption {
  oid: string;
  name: string | null;
  email: string | null;
}

/**
 * Everyone the call log can be filtered on, by name from A: everyone the
 * connector kept a call of (`GET /admin/mcp/users`, whose `last_seen_at` is
 * their last kept call), with their name where the usage has it, and the
 * usage's people it does not list. Someone known only by a block, without a
 * call, is no choice: the log would always be empty. Calls without sign-in
 * have no id to filter on.
 */
export function personOptions(
  users: readonly McpConnectorUser[],
  usage: readonly McpUserUsage[],
): PersonOption[] {
  const names = new Map(
    usage.flatMap((u) => (u.user_oid ? [[u.user_oid, u.user_name?.trim() || null] as const] : [])),
  );
  const options = new Map<string, PersonOption>();
  for (const user of users) {
    if (!user.last_seen_at) {
      continue;
    }
    options.set(user.user_oid, {
      oid: user.user_oid,
      name: names.get(user.user_oid) ?? null,
      email: user.user_email ?? null,
    });
  }
  for (const used of usage) {
    if (used.user_oid && !options.has(used.user_oid)) {
      options.set(used.user_oid, {
        oid: used.user_oid,
        name: used.user_name?.trim() || null,
        email: used.user_email ?? null,
      });
    }
  }
  return sortRows([...options.values()], { key: "name", dir: "asc" }, personOptionName);
}

/** How a person shows among the choices: their name, else their email, else their Microsoft id. */
export function personOptionName(person: PersonOption): string {
  return person.name ?? person.email ?? person.oid;
}

/**
 * The tools the call log can be filtered on: the catalogue's served tools in
 * its order, then any tool the usage holds that the catalogue no longer lists.
 */
export function toolNames(catalogue: readonly ConnectorTool[], used: readonly string[]): string[] {
  const served = catalogue.filter((tool) => tool.status === "available").map((tool) => tool.name);
  return [...new Set([...served, ...used])];
}

/** The 1-based rows a page shows ("26–50"), or 0–0 for an empty page. */
export function pageRange(
  offset: number,
  count: number,
  total: number,
): { from: number; to: number } {
  if (count === 0) {
    return { from: 0, to: 0 };
  }
  return { from: offset + 1, to: Math.min(offset + count, total) };
}

/**
 * Where to go when a page came back empty past the end (at midnight the window
 * moves on and older calls drop out): the last page that still has calls, or
 * null when the page is fine as it is.
 */
export function offsetBackInRange(
  page: Pick<McpToolCallPage, "items" | "total" | "offset">,
  pageSize: number,
): number | null {
  if (page.items.length > 0 || page.offset === 0) {
    return null;
  }
  const last = page.total > 0 ? Math.floor((page.total - 1) / pageSize) * pageSize : 0;
  return last < page.offset ? last : null;
}

/** What a page of Statistieken needs to know of a query to decide what to show. */
interface QueryState {
  isError: boolean;
  isPlaceholderData: boolean;
  data: unknown;
}

/**
 * "failed" when a query failed with nothing current to show (a first load, or
 * a new period still showing the previous one), "stale" when only a refresh
 * failed and the data on screen is still right, else "ready".
 */
export function loadState(...queries: QueryState[]): "ready" | "stale" | "failed" {
  if (queries.some((q) => q.isError && (q.data === undefined || q.isPlaceholderData))) {
    return "failed";
  }
  return queries.some((q) => q.isError) ? "stale" : "ready";
}

/** Between a number and its unit, so "1,2 s" never breaks over two lines. */
const NO_BREAK = "\u00a0";

/**
 * A duration in milliseconds: "85 ms", "1,2 s" ("1.2 s" in English), or "—"
 * when unknown, the unit never on a line of its own.
 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) {
    return "—";
  }
  if (ms < 1000) {
    return `${formatNumber(Math.round(ms))}${NO_BREAK}ms`;
  }
  const seconds = formatNumber(ms / 1000, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return `${seconds}${NO_BREAK}s`;
}

/** One row of the per-tool table: a tool's calls in the period, with what it does. */
export interface ToolRow extends McpToolUsage {
  /** The tool's title and one-line summary; null for a tool the catalogue does not know. */
  title: string | null;
  summary: string | null;
  /** The tool's group in the catalogue; null for a tool it does not know. */
  group: string | null;
}

/**
 * Every tool, so the table always says what each one does: the
 * period's tools first, busiest first as the API sends them, then the
 * catalogue's other tools (`GET /mcp/overview`) in its order with 0 calls. Each
 * gets the catalogue's title, summary and group; a tool that is no longer
 * served, or a catalogue that has not loaded, leaves them null: the row then
 * shows the tool's name alone.
 */
export function toolRows(usage: McpToolUsage[], catalogue: ConnectorTool[]): ToolRow[] {
  const byName = new Map(catalogue.map((tool) => [tool.name, tool]));
  const used = new Set(usage.map((u) => u.tool));
  const unused: McpToolUsage[] = catalogue
    .filter((tool) => !used.has(tool.name))
    .map((tool) => ({
      tool: tool.name,
      calls: 0,
      errors: 0,
      mean_duration_ms: null,
    }));
  return [...usage, ...unused].map((row) => {
    const known = byName.get(row.tool);
    return {
      ...row,
      title: known?.title ?? null,
      summary: known?.summary ?? null,
      group: known?.group ?? null,
    };
  });
}
